
import { memo } from 'react'
import Link from 'next/link'
import { Panel } from '@xyflow/react'
import { Icon } from '@/components/ui/Icon'
import { formatW } from '@/lib/power'
import type { ShowSummary as ShowSummaryData } from '@/lib/show-summary'
import { cx } from '@/lib/ui'
import { useUiMode } from '@/components/workspace/UiModeProvider'

type ShowSummaryProps = {
  summary: ShowSummaryData
  /** Outputs feeding more than one cable, drawn before an output took one. */
  sharedOutputs: number
  /** "/w/<slug>/power?from=<project>" — where the full load schedule is built. */
  powerHref: string
}

/**
 * The show at a glance, pinned to the bottom-right of the drawing surface.
 *
 * Power leads because it is the number people are asked for first ("what do we
 * need from the venue?"). It is peak watts at the wall for every device with a
 * published figure, and the line beneath it says how many devices have none —
 * a total that quietly omits four devices is worse than no total, so the
 * caveat sits directly under the number where a screenshot cannot lose it.
 */
function ShowSummaryComponent({ summary, sharedOutputs, powerHref }: ShowSummaryProps) {
  const { advanced } = useUiMode()
  const { power, cables, devices } = summary
  const hasFigure = power.counted > 0
  const powerCaveat =
    power.unknown > 0
      ? `${power.unknown} device${power.unknown === 1 ? '' : 's'} without a published figure`
      : hasFigure
        ? `${power.counted} device${power.counted === 1 ? '' : 's'}, all published`
        : 'no published figures yet'

  return (
    <Panel
      position="bottom-right"
      role="group"
      aria-label="Show at a glance"
      className="glass w-52 rounded-2xl p-1.5 max-md:hidden"
    >
      <Link
        href={powerHref}
        className="block rounded-xl px-2.5 pb-2 pt-1.5 transition-colors hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        title={
          hasFigure
            ? `Peak draw at the wall, from manufacturer-published figures${
                power.fedElsewhere > 0
                  ? ` — ${power.fedElsewhere} device${power.fedElsewhere === 1 ? '' : 's'} powered by another device or a battery are not counted`
                  : ''
              }. Open the power plan for supply and circuits.`
            : 'No placed device has a published draw yet. Open the power plan to size the supply another way.'
        }
      >
        <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted">Power draw</p>
        <p className="mt-0.5 flex items-baseline gap-1.5">
          <span className="text-xl font-semibold tabular-nums tracking-tight">
            {hasFigure ? formatW(power.wallMaxW) : '—'}
          </span>
          {hasFigure && <span className="text-[10px] text-muted">peak</span>}
          <Icon name="arrowRight" className="ml-auto h-3.5 w-3.5 self-center text-muted" />
        </p>
        <p className={cx('text-[10px] leading-snug', power.unknown > 0 ? 'text-warning' : 'text-muted')}>
          {powerCaveat}
        </p>
      </Link>

      <dl className="space-y-0.5 border-t border-[var(--border)] px-2.5 pt-1.5 pb-1 text-[11px]">
        <Row
          label="Devices"
          value={String(devices.count)}
          note={devices.unconnected > 0 ? `${devices.unconnected} unconnected` : null}
          tone="warn"
        />
        <Row
          label="Cables"
          value={String(cables.count)}
          note={cables.overReach > 0 ? `${cables.overReach} over reach` : null}
          tone="danger"
        />
        <Row
          label="Cable run"
          value={cables.totalMetres > 0 ? `${Math.round(cables.totalMetres)} m` : '—'}
          note={cables.unmeasured > 0 ? `${cables.unmeasured} unmeasured` : null}
          tone="warn"
        />
        {/* Rack units answer "will it fit the flightcase", which is a question
            only someone with a rack is asking. */}
        {advanced && devices.rackUnits > 0 && (
          <Row label="Rack space" value={`${devices.rackUnits} RU`} note={null} tone="warn" />
        )}
      </dl>
      {/* A consequence, so it shows in Simple mode too, with the fix in it. */}
      {sharedOutputs > 0 && (
        <p className="border-t border-[var(--border)] px-2.5 pt-1.5 pb-1 text-[10px] leading-snug text-warning">
          {sharedOutputs === 1 ? 'One output feeds' : `${sharedOutputs} outputs feed`} two or more cables. An output
          takes one: right-click a cable and choose Split.
        </p>
      )}
    </Panel>
  )
}

function Row({
  label,
  value,
  note,
  tone,
}: {
  label: string
  value: string
  note: string | null
  tone: 'warn' | 'danger'
}) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-muted">{label}</dt>
      <dd className="ml-auto flex items-baseline gap-1.5 font-mono tabular-nums">
        {note && (
          <span className={cx('font-sans text-[10px]', tone === 'danger' ? 'text-danger' : 'text-warning')}>
            {note}
          </span>
        )}
        <span>{value}</span>
      </dd>
    </div>
  )
}

export const ShowSummary = memo(ShowSummaryComponent)
